// The only module that calls the admin API. Pages use AdminClient and never call fetch.
import type {
  AdminScope,
  AuditPage,
  Crate,
  DeletedBody,
  Domain,
  DomainDeleted,
  DomainMethod,
  ErrorBody,
  DeleteImpact,
  GraceCollection,
  ImpactCollection,
  Grant,
  GrantInput,
  Invite,
  Member,
  ItemList,
  Me,
  MemberAdded,
  MemberList,
  Namespace,
  Pending,
  PendingMemberList,
  Principal,
  PrincipalCreated,
  PrincipalInput,
  Role,
  Tenant,
  TenantDetail,
  TenantSummary,
  TrustConfig,
  TrustConfigInput,
  TrustTestResult,
  OkBody,
  OciManifestRestored,
  OciRepoSettings,
  OciRepository,
  OciTag,
  PackageItem,
} from "./types";

/** A tag or a manifest of an OCI repository. */
export type OciPart = { kind: "tag"; tag: string } | { kind: "manifest"; digest: string };

function ociPart(p: OciPart): string {
  return p.kind === "tag" ? `tags/${encodeURIComponent(p.tag)}` : `manifests/${encodeURIComponent(p.digest)}`;
}

/** Error from the admin API, with the documented code (api.md section 1). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
    /** Set on a `409` create that collides with an object pending deletion (api.md 2.8). */
    readonly pendingDeletion: { id: string; purge_after: string } | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ClientOptions {
  /** Base URL of the STS host, for example https://sts.prego.k8skill.com. */
  baseUrl: string;
  /** Returns a current Firebase ID token. */
  getIdToken: () => Promise<string>;
  fetch?: typeof fetch;
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

/** Typed client for the admin API. */
export function createAdminClient(opts: ClientOptions) {
  const base = opts.baseUrl.replace(/\/+$/, "");
  const doFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);

  async function call<T>(method: Method, path: string, body?: unknown): Promise<T> {
    const token = await opts.getIdToken();
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    let res: Response;
    try {
      res = await doFetch(`${base}/v1/admin${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError(0, "network", "Cannot reach the Prego API. Check your connection.", null);
    }
    const requestId = res.headers.get("X-Request-Id");
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }
    if (!res.ok) {
      const err = (data as ErrorBody | null)?.error;
      throw new ApiError(
        res.status,
        err?.code ?? "internal",
        err?.message ?? `Request failed with status ${res.status}.`,
        err?.request_id ?? requestId,
        err?.pending_deletion ?? null,
      );
    }
    return data as T;
  }

  const t = (tenant: string) => `/tenants/${encodeURIComponent(tenant)}`;
  const id = encodeURIComponent;

  return {
    me: () => call<Me>("GET", "/me"),
    createTenant: (body: { slug: string; name: string; plan?: "consumer" }) =>
      call<Tenant>("POST", "/tenants", body),
    getTenant: (tenant: string) => call<TenantDetail>("GET", t(tenant)),
    /** Counts, storage and downloads of a tenant. Admins and owners only; others get 403. */
    getTenantSummary: (tenant: string) => call<TenantSummary>("GET", `${t(tenant)}/summary`),

    listMembers: (tenant: string) => call<MemberList>("GET", `${t(tenant)}/members`),
    addMember: (tenant: string, body: { email: string; role: Role }) =>
      call<MemberAdded>("POST", `${t(tenant)}/members`, body),
    /** Marks the member pending deletion (registry 0.20.0, ADR-0031). Access ends at once. */
    removeMember: (tenant: string, userId: string) =>
      call<DeletedBody>("DELETE", `${t(tenant)}/members/${id(userId)}`),
    /** Cancels an invite (ADR-0024 point 3). It is pending deletion and does not bind (registry 0.20.0). */
    cancelInvite: (tenant: string, invId: string) =>
      call<DeletedBody>("DELETE", `${t(tenant)}/members/invites/${id(invId)}`),
    listPendingMembers: (tenant: string) =>
      call<PendingMemberList>("GET", `${t(tenant)}/members?state=pending_deletion`),
    restoreMember: (tenant: string, userId: string) =>
      call<Member>("POST", `${t(tenant)}/members/${id(userId)}/restore`),
    /** Hard delete now. Humans only. `confirm` is the member email. */
    purgeMember: (tenant: string, userId: string, confirm: string) =>
      call<OkBody>("POST", `${t(tenant)}/members/${id(userId)}/purge`, { confirm }),
    restoreInvite: (tenant: string, invId: string) =>
      call<Invite>("POST", `${t(tenant)}/members/invites/${id(invId)}/restore`),
    /** Hard delete now. Humans only. `confirm` is the invite email. */
    purgeInvite: (tenant: string, invId: string, confirm: string) =>
      call<OkBody>("POST", `${t(tenant)}/members/invites/${id(invId)}/purge`, { confirm }),

    listNamespaces: (tenant: string) => call<ItemList<Namespace>>("GET", `${t(tenant)}/namespaces`),
    createNamespace: (tenant: string, body: { slug: string; description?: string }) =>
      call<Namespace>("POST", `${t(tenant)}/namespaces`, body),

    listTrustConfigs: (tenant: string) => call<ItemList<TrustConfig>>("GET", `${t(tenant)}/trust-configs`),
    createTrustConfig: (tenant: string, body: TrustConfigInput) =>
      call<TrustConfig>("POST", `${t(tenant)}/trust-configs`, body),
    deleteTrustConfig: (tenant: string, tcId: string) =>
      call<DeletedBody>("DELETE", `${t(tenant)}/trust-configs/${id(tcId)}`),
    testTrustConfig: (tenant: string, tcId: string, token: string) =>
      call<TrustTestResult>("POST", `${t(tenant)}/trust-configs/${id(tcId)}/test`, { token }),

    listPrincipals: (tenant: string) => call<ItemList<Principal>>("GET", `${t(tenant)}/principals`),
    createPrincipal: (tenant: string, body: PrincipalInput) =>
      call<PrincipalCreated>("POST", `${t(tenant)}/principals`, body),
    updatePrincipal: (tenant: string, prnId: string, body: { name?: string; disabled?: boolean }) =>
      call<Principal>("PATCH", `${t(tenant)}/principals/${id(prnId)}`, body),
    /** Sets the admin scopes of a principal; null removes them (ADR-0028). Humans only. */
    setAdminScopes: (tenant: string, prnId: string, scopes: AdminScope[] | null) =>
      call<Principal>("PATCH", `${t(tenant)}/principals/${id(prnId)}`, { admin_scopes: scopes }),
    revokePrincipal: (tenant: string, prnId: string) =>
      call<OkBody>("POST", `${t(tenant)}/principals/${id(prnId)}/revoke`),
    deletePrincipal: (tenant: string, prnId: string) =>
      call<DeletedBody>("DELETE", `${t(tenant)}/principals/${id(prnId)}`),

    listGrants: (tenant: string) => call<ItemList<Grant>>("GET", `${t(tenant)}/grants`),
    createGrant: (tenant: string, body: GrantInput) => call<Grant>("POST", `${t(tenant)}/grants`, body),
    deleteGrant: (tenant: string, grtId: string) =>
      call<DeletedBody>("DELETE", `${t(tenant)}/grants/${id(grtId)}`),

    // Delete grace period (ADR-0031, api.md 2.8): grants, principals, trust configs, domains and namespaces (by ID).
    /** Marks the object pending deletion. It stops working at once; restore undoes it until `purge_after`. */
    deleteObject: (tenant: string, collection: GraceCollection, objId: string, confirm?: string) =>
      call<DeletedBody & Partial<Omit<DomainDeleted, "ok">>>(
        "DELETE",
        `${t(tenant)}/${collection}/${id(objId)}${confirm === undefined ? "" : `?confirm=${encodeURIComponent(confirm)}`}`,
      ),
    /** What a delete would cut off (api.md 2.8, registry 0.19.0). Admins only. */
    getImpact: (tenant: string, collection: ImpactCollection, objId: string) =>
      call<DeleteImpact>("GET", `${t(tenant)}/${collection}/${id(objId)}/impact`),
    /** Only the objects pending deletion, each with `deleted_at`, `deleted_by` and `purge_after`. */
    listPending: <T>(tenant: string, collection: GraceCollection) =>
      call<ItemList<Pending<T>>>("GET", `${t(tenant)}/${collection}?state=pending_deletion`),
    /** Undoes a delete. Returns the object unchanged. 409 when a parent is still pending deletion. */
    restoreObject: <T>(tenant: string, collection: GraceCollection, objId: string) =>
      call<T>("POST", `${t(tenant)}/${collection}/${id(objId)}/restore`),
    /** "Delete now": hard delete with no undo. Humans only. `confirm` is the name (the ID for a grant). */
    purgeObject: (tenant: string, collection: GraceCollection, objId: string, confirm: string) =>
      call<OkBody>("POST", `${t(tenant)}/${collection}/${id(objId)}/purge`, { confirm }),

    // Verified domains (ADR-0030, api.md 2.7). Human only. Delete goes through deleteObject with `confirm`.
    listDomains: (tenant: string) => call<ItemList<Domain>>("GET", `${t(tenant)}/domains`),
    /** `sign_in` verifies at once or gives 400; `dns_txt` returns state `pending` with `txt_name` and `txt_value`. */
    addDomain: (tenant: string, body: { domain: string; method: DomainMethod }) =>
      call<Domain>("POST", `${t(tenant)}/domains`, body),
    /** Runs the DNS check now. 10 per hour per tenant: then `429`. */
    verifyDomain: (tenant: string, domId: string) =>
      call<Domain>("POST", `${t(tenant)}/domains/${id(domId)}/verify`),

    listCrates: (tenant: string, ns?: string) =>
      call<ItemList<Crate>>("GET", `${t(tenant)}/crates${ns ? `?ns=${id(ns)}` : ""}`),

    // Packages and OCI repositories (api.md 4.1, registry 0.18.0). Changes need role admin.
    /** One list for both types. `pending` lists the OCI repositories pending deletion. */
    listPackages: (tenant: string, opts: { type?: "cargo" | "oci"; pending?: boolean } = {}) => {
      const q = new URLSearchParams();
      if (opts.type) q.set("type", opts.type);
      if (opts.pending) q.set("state", "pending_deletion");
      const qs = q.toString();
      return call<ItemList<PackageItem>>("GET", `${t(tenant)}/packages${qs ? `?${qs}` : ""}`);
    },
    /** `pending` gives the tags and manifests pending deletion instead of the live ones. */
    getOciRepository: (tenant: string, repId: string, pending = false) =>
      call<OciRepository>(
        "GET",
        `${t(tenant)}/oci/repositories/${id(repId)}${pending ? "?state=pending_deletion" : ""}`,
      ),
    updateOciRepository: (tenant: string, repId: string, body: Partial<OciRepoSettings>) =>
      call<{ id: string; settings: OciRepoSettings }>(
        "PATCH",
        `${t(tenant)}/oci/repositories/${id(repId)}`,
        body,
      ),
    /** `confirm` is the repository name (the repo part, without tenant and namespace). */
    deleteOciRepository: (tenant: string, repId: string, confirm: string) =>
      call<DeletedBody>(
        "DELETE",
        `${t(tenant)}/oci/repositories/${id(repId)}?confirm=${encodeURIComponent(confirm)}`,
      ),
    restoreOciRepository: (tenant: string, repId: string) =>
      call<unknown>("POST", `${t(tenant)}/oci/repositories/${id(repId)}/restore`),
    purgeOciRepository: (tenant: string, repId: string, confirm: string) =>
      call<OkBody>("POST", `${t(tenant)}/oci/repositories/${id(repId)}/purge`, { confirm }),
    /** `part` is `tags/<tag>` or `manifests/<digest>`. */
    deleteOciPart: (tenant: string, repId: string, part: OciPart) =>
      call<DeletedBody>("DELETE", `${t(tenant)}/oci/repositories/${id(repId)}/${ociPart(part)}`),
    restoreOciPart: (tenant: string, repId: string, part: OciPart) =>
      call<OciTag | OciManifestRestored>(
        "POST",
        `${t(tenant)}/oci/repositories/${id(repId)}/${ociPart(part)}/restore`,
      ),
    /** `confirm` is the tag, or the full digest of a manifest. */
    purgeOciPart: (tenant: string, repId: string, part: OciPart, confirm: string) =>
      call<OkBody>("POST", `${t(tenant)}/oci/repositories/${id(repId)}/${ociPart(part)}/purge`, { confirm }),

    listAudit: (
      tenant: string,
      opts: { limit?: number; before?: string; actorKind?: "user" | "principal" } = {},
    ) => {
      const q = new URLSearchParams();
      if (opts.limit) q.set("limit", String(opts.limit));
      if (opts.before) q.set("before", opts.before);
      if (opts.actorKind) q.set("actor_kind", opts.actorKind);
      const qs = q.toString();
      return call<AuditPage>("GET", `${t(tenant)}/audit${qs ? `?${qs}` : ""}`);
    },
  };
}

export type AdminClient = ReturnType<typeof createAdminClient>;
